import React, { useState, useEffect } from "react";
import {
    StyledButton,
    StyledClearButton,
    StyledLink,
    StyledSelectContent,
    StyledSelectWrapper,
    StyledSpinnerWrapper,
    StyledSyncIcon,
    StyledDropdownWrapper
} from "../styles/styledComponents";

import Button from "./Button";
import Dropdown from "./Dropdown";
import "../styles/selectWrapper.css";
import colors from "../styles/colors";
import { FormProvider, useForm } from "react-hook-form";
import ResultTable from "./ResultTable";
import Calculate from "../types/Calculate";
import Field from "../components/Field";
import MountingTypeField from "../types/MountingTypeField";
import ProductCategory from "../types/ProductCategory";
import Command from "../types/Command";
import FieldValue from "../types/FieldValue";
import ApiDataService from "../services/ApiDataService";
import { useMutation } from "react-query";
import ResultValue from "../types/ResultValue";
import styled from "styled-components";
import { useSelectedContextState } from "../context/selectedContext";
import Checkbox from "../components/Checkbox";
import { faSpinner, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import TableRowType from "../types/TableRowType";
import Row from "../types/Row";
import SelectedMountingType from "../types/SelectedMountingType";
import SelectedColor from "../types/SelectedColor";
import { getTotalDto, calculateNetPrice } from "../helpers/ResultsHelper";
import { generateResultsTablePdf } from "../helpers/PdfHelper";
import ProfileDimensions from "../types/ProfileDimensions"
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import { parse } from 'csv-parse';

import profiltopparSe from '../data/profiltoppar.se.csv'
import profiltopparNo from '../data/profiltoppar.no.csv'
import falsatSe from '../data/falsat.se.csv'
import falsatNo from '../data/falsat.no.csv'
import { MountingTypeCode } from "../types/MountingTypeCode";
import SelectedProduct from "../types/SelectedProduct";

const csvByCountry: Record<string, { profiled: string; folded: string }> = {
    se: { profiled: profiltopparSe, folded: falsatSe },
    no: { profiled: profiltopparNo, folded: falsatNo },
};

// A field (key = field name) is hidden while the checkbox field named in the value is checked.
// Matched by field `name` (stable across countries), lowercased. Add more pairs as needed.
const HIDE_FIELD_WHEN_CHECKED: Record<string, string> = {
    "brackets-multiplication-factor": "use-support-feet",
};

export const StyledLeftSpinnerWrapper = styled(StyledSpinnerWrapper)`
  justify-content: flex-start;
  padding-left: 0;
  margin-bottom: 30px;
`;

const StyledAddButton = styled(StyledButton)`
  margin-right: 20px;
  font-size: 0.9em;
`;

type MeasurementWrapperProps = {
    header: string;
    mountingTypeFields?: MountingTypeField[];
    product?: SelectedProduct;
    countryCode: string;
    hideMounted: boolean;
    color?: SelectedColor;
    mountingType?: SelectedMountingType;
};

const MeasurementWrapper = (props: MeasurementWrapperProps) => {
    const {
        header,
        mountingTypeFields,
        product,
        countryCode,
        mountingType,
        hideMounted,
        color
    } = props;

    const [calculate, setCalculate] = useState<Calculate>({});
    const [results, setResults] = useState<ResultValue[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    
    const [informationSign, setInformationSign] = useState<ResultValue[]>([]);

    const [summaryText, setSummaryText] = useState<string>('');

    const [isLoadingPdf, setLoadingPdf] = useState(false);
    const [formValid] = useState(false);
    const [state, dispatch] = useSelectedContextState();
    const { t } = useTranslation("trans");
    const { measurementsLoading, summaryTable } = state;
    const [profiledSheetRows, setProfiledSheetRows] = useState<ProfileDimensions[]>([]);
    const [foldedRows, setFoldedRows] = useState<ProfileDimensions[]>([]);
    const methods = useForm();

    const [shouldRenderTable, setShouldRenderTable] = useState<boolean>(false);
    const [discountValue, setDiscountValue] = useState<number>(0);
    let discount = 0;

    useEffect(() => {

        const data = csvByCountry[countryCode?.toLowerCase()] ?? csvByCountry.se;
        loadCSV(data.profiled, setProfiledSheetRows);
        loadCSV(data.folded, setFoldedRows);

    }, [countryCode]);

    useEffect(() => {
        if (mountingTypeFields && mountingTypeFields.length > 0) {
            setShouldRenderTable(true);
        }
        else {
            setShouldRenderTable(false);
        }
    }, [mountingTypeFields])


    const loadCSV = (data: string, setData: (arg: ProfileDimensions[]) => void) => (
        fetch(data)
            .then(response => response.text())
            .then(text => {
                parse(text, {
                    delimiter: ';',
                    columns: ["profile", "dimensions_cc_attachment"],
                    from_line: 2,
                }, (error: Error | undefined, result: ProfileDimensions[]) => {
                    if (error) {
                        console.error(error);
                    }
                 
                    setData(result);
                });
            })
    )


    const { isLoading: isLoadingCalculate, mutate: postCalculate } = useMutation<
        any,
        Error
    >(
        async () => {
            let mountingTypeField: MountingTypeField = mountingTypeFields![0];
            let productCategory: ProductCategory | undefined = mountingTypeField?.productCategory;
            let commandObj: Command | undefined = productCategory?.command;
            let command: string | undefined = commandObj?.calculationCommand?.toLowerCase();
          
            if (command) {
                calculate.type = command;
                return await ApiDataService.calculate(calculate, countryCode);
            }
        }
        ,
        {
            onSuccess: (res) => {
                if (res !== undefined){
                    setErrors(res.success === false && res.message ? res.message.split(';') : []);
                    res.data.forEach((item: any) => {
                        item.netPrice = calculateNetPrice(item.amount, item.price, discountValue);
                    });
                    res.data = res.data.sort((a: ResultValue, b: ResultValue) => {
                        var aa = a.sortOrder != null ? a.sortOrder : 0;
                        var bb = b.sortOrder != null ? b.sortOrder : 0;
                        return aa - bb;
                    });

                    setResults(res.data);    
                }
                
             },
            onError: (err: any) => {
                // console.log(err.response?.data || err);
            },
        }
    );

    const onSubmit = async (data: any, e: any) => {
        try {
            let fieldValueArr: FieldValue[] = [];
            let summaryFields: string[] = []
            setSummaryText('');

            if (mountingTypeFields) {
                for (var i = 0; i < mountingTypeFields.length; i++) {
                    var field = {} as FieldValue;
                    let id = e.target[i].id;

                    field.id = id;
                    let mountingField = mountingTypeFields.find(
                        (element) => element.id === Number(id)
                    );
                    field.name = mountingField?.name?.toLowerCase();

                    if (mountingField?.type?.toLowerCase() === "checkbox") {
                        field.value = e.target[i].checked.toString();
                    } else {
                        // NO and SE write decimals with a comma and the field regex allows
                        // it, but the backend parses invariant-culture, where a comma is a
                        // thousands separator: "5,5" is read as 55 and silently inflates the
                        // result. Normalise to a period, the same way the Snowzone and
                        // Snowbarrier-short calculators already do on input.
                        field.value = e.target[i].value?.replaceAll(",", ".");
                    }
                    
                    // A field hidden by its controlling checkbox is still sent to the
                    // calculation (the backend ignores it), but it must not end up in the
                    // summary text or the PDF — the user never got to see it.
                    if (mountingField && isFieldHidden(mountingField)) {
                        fieldValueArr.push(field);
                        continue;
                    }

                    const fieldName = mountingField?.name?.toLowerCase();
                    const fieldType = mountingField?.type?.toLowerCase();

                    if (fieldType === "dropdown") {
                        var json = JSON.parse(mountingField?.placeholder!);

                        var placeHoldervalue = json?.filter((x: any) => {
                            return x.value == field.value;
                        })[0];
                        summaryFields.push(mountingField?.label + ": " + placeHoldervalue.label);
                    }
                    else if (fieldName !== "discount") {
                        if (fieldType === "checkbox") {
                            summaryFields.push(mountingField?.label + ": " + t("field.checkbox-" + field.value));
                        } else {

                            summaryFields.push(mountingField?.label + ": " + field.value);
                        }
                    }

                    // Only the discount field sets the discount. It used to be set by every
                    // dropdown as well, and came out right purely because Discount happens
                    // to sort last in the field data.
                    if (fieldName === "discount") {
                        discount = parseInt(field.value || "0");
                        setDiscountValue(discount);
                    }

                    fieldValueArr.push(field);
                }
            }

            // Only add text values or values > 0
            let summaryOfFields = summaryFields.filter(f => {
                const match = f.match(/:\s*(.+)$/);
                return !match || isNaN(Number(match[1])) || Number(match[1]) > 0;
            }).join(', ');
            
            const textSummary = hideMounted ? color?.name + ", " + summaryOfFields : t("selectTitles.mounted").toLowerCase() + " " + mountingType?.name?.toLowerCase() + ", " + color?.name + ", " + summaryOfFields;
            setSummaryText(textSummary);

            let calc = {} as Calculate;
            calc.fields = fieldValueArr;
            calc.colorId = color?.id;
            calc.countryCode = countryCode;
            calc.mountingTypeId = mountingType?.id;
            calc.mountingTypeCode = mountingType?.code;

            setCalculate(calc);

            try {
                postCalculate();
                await getInformationSign(discount);
            } catch (err) {
                //
            }
        } catch (e) { }
    };

    const getInformationSign = async (discount: number) => {
        const sign = await ApiDataService.getInformationSign(countryCode);
        if (sign != null) {
            sign.forEach((item: any) => {
                item.netPrice = calculateNetPrice(item.amount, item.price, discount);
            });
            setInformationSign(sign);    
        }
    }

    const clear = () => {
        dispatch({ type: "setClearAll", payload: true });
        setResults([]);
    };

    const mapItemToRow = (item: ResultValue): Row => {

        return {
            art: item.code || "",
            term: item.name || "",
            amount: item.amount || 0,
            discount: discountValue || 0,
            fullPrice: item.price || 0,
            weight: item.weight || 0,
            netPrice: item.netPrice || 0,
            sortOrder: item.sortOrder || 0
        }
    }

    const getInformationSignRow = (): TableRowType => {
        return {
            name: t("products.informationSign"),
            description: "",
            netPrice: informationSign.reduce((a, b) => a + (b.netPrice ? b.netPrice : 0), 0),
            row: informationSign.map(mapItemToRow)
        }
    }
    
    const addProduct = () => {
        dispatch({ type: "setSummaryTableLoading", payload: true });
        let currentList = [...summaryTable];
        let tableRow: TableRowType = {
            name: product?.name + ", " + (hideMounted ? '' : mountingType?.name + ", ") + color?.name,
            description: summaryText,
            netPrice: results.reduce((a, b) => a + (b.netPrice ? b.netPrice : 0), 0),
            row: results.map(mapItemToRow)
        }

        currentList.push(tableRow);
        if (currentList.filter((x) => x.name === t("products.informationSign")).length == 0) {
            currentList.push(getInformationSignRow())
        }
        dispatch({ type: "setSummaryTable", payload: currentList });
    }

    const total = getTotalDto([...informationSign, ...results], discount);

    const triggerPdfDownload = () => {
        setLoadingPdf(true);
        const fixedFiledName = (product?.name.replace('�', 'a').replace('�', 'a').replace('�', 'o'))?.toLowerCase();
        const fileName = (fixedFiledName) + "_" + new Date().toLocaleDateString() + ".pdf";
        const mergedResult = [...results, ...informationSign]
        mergedResult.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        generateResultsTablePdf({ results: mergedResult, discount: discountValue, total, productName: product?.name, summaryText, countryCode }, fileName).then(() => setLoadingPdf(false));
    };

    // Subscribe to all form values so the component re-renders when a checkbox toggles.
    const watchedValues = methods.watch();

    // True when `item` should be hidden because its controlling checkbox is checked.
    const isFieldHidden = (item: MountingTypeField): boolean => {
        const controllingName = item.name
            ? HIDE_FIELD_WHEN_CHECKED[item.name.toLowerCase()]
            : undefined;
        if (!controllingName) return false;

        const checkbox = mountingTypeFields?.find(
            (f) =>
                f.type?.toLowerCase() === "checkbox" &&
                f.name?.toLowerCase() === controllingName
        );
        if (!checkbox) return false;

        // react-hook-form's watch is undefined on first render for a `defaultChecked`
        // input, so fall back to the field's defaultValue (that's what makes NO,
        // where the checkbox is checked by default, hide the dropdown on first paint).
        const watched = watchedValues[checkbox.id.toString()];
        return watched === undefined ? Boolean(checkbox.defaultValue) : Boolean(watched);
    };

    const renderField = (item: MountingTypeField) => {
        const type = item.type?.toLowerCase()
        if (type === "checkbox") {
            return <Checkbox field={item} />;
        }
        if (type === "dropdown") {
            return <Dropdown field={item} />;
        }
        return <Field field={item} />;
    }

    const getTableForRows = (rows: ProfileDimensions[]) => (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 300 }} size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("table.profile")}</TableCell>
                        <TableCell align="right">{t("table.dimensions_cc_attachment")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row: ProfileDimensions) => (
                        <TableRow
                            key={row.profile}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <TableCell align="left">{row.profile}</TableCell>
                            <TableCell align="right">{row.dimensions_cc_attachment}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )

    const renderTable = () => {
        if (product?.showCcTable) {
            if (mountingType?.code == MountingTypeCode.S_PROFILED_SHEET) {
                return getTableForRows(profiledSheetRows)
            }
            else if (mountingType?.code == MountingTypeCode.S_FOLDED) {
                return getTableForRows(foldedRows)
            }
        }
        return <></>
    }

    return (
        <StyledSelectWrapper
            className={formValid ? "triangleSelected" : "triangleUnSelected"}
            style={{
                background: formValid ? colors.selectedBg : colors.unSelectedBg,
                borderColor: formValid
                    ? colors.selectedBorder
                    : colors.unSelectedBorder,
            }}
        >
            <StyledSelectContent>
                <h2>{header}</h2>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <div
                        style={{
                            display: "flex",
                            marginBottom: "60px",
                            flexDirection: "column",
                        }}
                    >
                        {measurementsLoading ? (
                            <StyledLeftSpinnerWrapper>
                                <CircularProgress color="inherit" />
                                {t("loading")}
                            </StyledLeftSpinnerWrapper>
                        ) : (
                            <FormProvider {...methods}>
                                <form
                                    onSubmit={methods.handleSubmit(onSubmit)}
                                    style={{ marginBottom: "20px" }}
                                >
                                    {mountingTypeFields
                                        ?.map((item, index) => (
                                            <div
                                                key={index}
                                                style={isFieldHidden(item) ? { display: "none" } : undefined}
                                            >
                                                {
                                                    renderField(item)
                                                }
                                            </div>
                                        ))}

                                    <Button
                                        hidden={color !== undefined}
                                        disabled={
                                            mountingTypeFields !== undefined &&
                                            mountingTypeFields?.length < 1
                                        }
                                        type="submit"
                                        text={t("buttons.calculate")}
                                        onClick={() => { }}
                                    />
                                </form>
                            </FormProvider>
                        )}
                    </div>
                    <div style={{
                        display: "flex",
                        marginRight: "100px",
                        flexDirection: "column",
                    }}>{shouldRenderTable ? renderTable() : <></>}
                    </div>
                </Grid>

                <div
                    style={{
                        display: "flex",
                        marginBottom: "60px",
                        flexDirection: "column",
                    }}
                >
                    {isLoadingCalculate ? (
                        <StyledLeftSpinnerWrapper>
                            <CircularProgress color="inherit" />
                            {t("calculating")}
                        </StyledLeftSpinnerWrapper>
                    ) : (
                        <><h3 style={{
                            marginBottom: "0px",
                        }}>{product?.name}</h3>
                            <p style={{ display: summaryText.length > 0 ? 'block' : 'none' }}>{product?.name}  {summaryText}</p>

                            {errors.length > 0 ? (
                                <div style={{position: 'relative',
                                    padding:'.75rem 1.25rem',
                                    marginBottom:'1rem',
                                    border:'1px solid transparent',
                                    borderRadius:'0.25rem',
                                    color: '#856404',
                                    backgroundColor: '#fff3cd',
                                    borderColor: '#ffeeba' }}>
                                    <h3>Följande fel uppstod:</h3>
                                    <ul style={{ listStyleType:'none' }}>
                                        {errors.map((item, index) => (
                                            <li>{t(`calculation-errors.${item}`)}</li>
                                        ))}
                                    </ul>
                                </div>
                            ): ""}

                            {/* Conditionally render the ResultTable based on errors */}
                            {errors.length === 0 && <ResultTable results={[...results, ...informationSign]} total={total} discount={discountValue} countryCode={countryCode} />}
                            
                            <div style={{ display: "inline" }}>
                                {errors.length === 0 &&
                                    <StyledAddButton disabled={results.length < 1} typeof="button" onClick={addProduct}>
                                    {t("buttons.addProduct").toUpperCase()}
                                    </StyledAddButton>
                                }

                                <StyledClearButton typeof="button" onClick={clear}>
                                    {t("buttons.newCalc").toUpperCase()}
                                    <StyledSyncIcon
                                        icon={faSyncAlt}
                                        size="lg"
                                        className="fas fa-flip-horizontal"
                                    />
                                </StyledClearButton>
                                {results.length > 0 ? (
                                    <StyledLink target={"_blank"} onClick={() => { triggerPdfDownload() }}>
                                        {isLoadingPdf ? (
                                            <StyledSyncIcon icon={faSpinner} size="lg" className="fas fa-refresh fa-spin" />
                                        ) : t('buttons.save_as_pdf').toUpperCase()}
                                    </StyledLink>
                                ) : <div></div>}
                            </div>
                        </>
                    )}
                </div>
            </StyledSelectContent>
        </StyledSelectWrapper>
    );
};

export default MeasurementWrapper;
